/**
 * URL search-param validators.
 *
 * 13-ui-screens.md §12.7 writes these as zod schemas, but **zod is not a
 * dependency of `apps/web`** (`apps/web/package.json` lists nine runtime deps
 * and zod is not among them; it resolves only inside `packages/shared`, which
 * uses it for request bodies). The build rule is "do not add dependencies", so
 * the schemas are hand-written coercers with the same semantics: unknown or
 * malformed input falls back to the documented default rather than throwing,
 * which is exactly what zod's `.catch(...)` does in the spec's snippet.
 *
 * Every route that uses one of these MUST also declare
 * `loaderDeps: ({ search }) => search` — `validateSearch` alone does not feed
 * the loader (00-stack.md §5.9.6).
 */

import { RECONCILIATION_ENABLED } from '@/lib/features';

/* ─────────────────────────── generic coercers ───────────────────────────── */

export function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Ids are echoed back to the API, so a non-string is dropped, never coerced. */
export function asId(value: unknown): string | undefined {
  return asString(value);
}

export function asEnum<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T,
): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

export function asOptionalEnum<T extends string>(
  value: unknown,
  allowed: readonly T[],
): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

export function asInt(value: unknown, fallback: number, min = 1): number {
  const n =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : Number.NaN;
  return Number.isFinite(n) && Number.isInteger(n) && n >= min ? n : fallback;
}

export function asBool(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
}

/* ────────────────────────── /payroll (the list) ─────────────────────────── */

export const RUN_STATUSES = [
  'DRAFT',
  'COMPUTED',
  'RECONCILED',
  'APPROVED_LOCKED',
  'SUPERSEDED',
] as const;
export type RunStatusFilter = (typeof RUN_STATUSES)[number];

export interface PayrollListSearch {
  periodId?: string;
  status?: RunStatusFilter;
  page: number;
}

export function validatePayrollListSearch(search: Record<string, unknown>): PayrollListSearch {
  const periodId = asId(search['periodId']);
  const status = asOptionalEnum(search['status'], RUN_STATUSES);
  return {
    ...(periodId ? { periodId } : {}),
    ...(status ? { status } : {}),
    page: asInt(search['page'], 1),
  };
}

/* ───────────────────────── /payroll/$runId (§12) ────────────────────────── */

export const PAYROLL_VIEWS = ['breakdown', 'adjustments', 'reconciliation', 'register'] as const;
export type PayrollView = (typeof PAYROLL_VIEWS)[number];

/**
 * The views a URL may name. `reconciliation` stays in the type and the search parser so
 * a bookmarked `?view=reconciliation` still parses — it falls back to `breakdown` below
 * while `RECONCILIATION_ENABLED` is off, rather than 404-ing a saved link.
 */
export const OFFERED_VIEWS: readonly PayrollView[] = PAYROLL_VIEWS.filter(
  (v) => v !== 'reconciliation' || RECONCILIATION_ENABLED,
);

export interface RunSearch {
  /** §12.7 — the view toggle. `breakdown` is the default (§12.4). */
  view: PayrollView;
  /** The selected worker in the left-hand list (§12.4 [N]). */
  worker?: string;
  /** The `reconciliation_baseline` uploaded from the workbook (T1). */
  baseline?: string;
  /** Free-text worker search in the left list / register. */
  q?: string;
  teamId?: string;
  page: number;
  /** §12.5 — `Show only variances outside tolerance`, on by default. */
  onlyVariances: boolean;
}

export function validateRunSearch(search: Record<string, unknown>): RunSearch {
  const worker = asId(search['worker']);
  const baseline = asId(search['baseline']);
  const q = asString(search['q']);
  const teamId = asId(search['teamId']);
  return {
    view: asEnum(search['view'], OFFERED_VIEWS, 'breakdown'),
    ...(worker ? { worker } : {}),
    ...(baseline ? { baseline } : {}),
    ...(q ? { q } : {}),
    ...(teamId ? { teamId } : {}),
    page: asInt(search['page'], 1),
    onlyVariances: asBool(search['onlyVariances'], true),
  };
}
